import PropTypes from 'prop-types';
import { minutesOfDay, minutesNowIn } from '../hooks/usePrayerTimes';
import { prayerShort } from '../prayers';

// The day as a curve, with the prayers set along it and a bright dot for now.
//
// It answers "where am I in the day" before a single number has been read,
// which is what someone glancing at their phone between other things actually
// wants.
//
// WHY A BEZIER RATHER THAN AN ARC
//
// This was an SVG arc: `A 158 158` between two points 316 apart, which is a
// semicircle, so its top sat 82 units above a box 88 units tall. With
// overflow visible -- needed so the end dots are not clipped -- it painted over
// the countdown above it, and the number the whole screen exists to show was
// the one thing covered up.
//
// A quadratic Bezier cannot do that. It is contained by its own control points,
// so choosing those chooses the height, and the curve stays inside the box by
// construction rather than by arithmetic that has to be redone whenever the
// width changes.
const W = 340, BASE = 86, PEAK_CTRL = -30;
const P0 = { x: 14, y: BASE };
const P1 = { x: W / 2, y: PEAK_CTRL };
const P2 = { x: W - 14, y: BASE };

// The curve reaches half way to its control point, so the actual top is
// (BASE + 2*PEAK_CTRL + BASE) / 4 -- about 14, comfortably inside the box.
const at = (t) => ({
  x: (1 - t) * (1 - t) * P0.x + 2 * (1 - t) * t * P1.x + t * t * P2.x,
  y: (1 - t) * (1 - t) * P0.y + 2 * (1 - t) * t * P1.y + t * t * P2.y,
});

// First light to last, rather than midnight to midnight: the hours nobody is
// awake for would otherwise take half the curve and squeeze the five prayers
// into the rest.
const DAY_START = 4 * 60;
const DAY_END = 22 * 60 + 30;

const progress = (min) =>
  Math.min(1, Math.max(0, (min - DAY_START) / (DAY_END - DAY_START)));

const DayArc = ({ prayers, tz, nextName }) => {
  const usable = (prayers || []).filter((p) => p.time24);
  if (usable.length === 0) return null;

  const now = minutesNowIn(tz);
  const tNow = progress(now);
  const here = at(tNow);

  // The travelled part is sampled rather than dash-offset, so it needs no path
  // length and is exact at any curvature.
  const points = [];
  const steps = Math.max(1, Math.round(tNow * 48));
  for (let i = 0; i <= steps; i += 1) {
    const p = at((tNow * i) / steps);
    points.push(`${p.x.toFixed(1)} ${p.y.toFixed(1)}`);
  }
  const travelled = 'M ' + points.join(' L ');

  return (
    <div className="arc">
      <svg viewBox={`0 0 ${W} ${BASE + 8}`} role="img"
           aria-label={`The day's prayers, with ${Math.round(tNow * 100)}% of the day elapsed.`}>
        <path className="arc__track"
              d={`M ${P0.x} ${P0.y} Q ${P1.x} ${P1.y} ${P2.x} ${P2.y}`} />
        {tNow > 0 && <path className="arc__done" d={travelled} />}

        {usable.map((p) => {
          const min = minutesOfDay(p.time24);
          const { x, y } = at(progress(min));
          const isNext = p.prayer === nextName;
          const minor = !prayerShort(p.prayer);
          const cls = 'arc__pip'
            + (isNext ? ' arc__pip--next' : min < now ? ' arc__pip--passed' : '');
          return (
            <g key={p.prayer}>
              <circle className={cls} cx={x} cy={y} r={isNext ? 4.5 : minor ? 2 : 3} />
              {!minor && (
                <text className="arc__label" x={x} y={y - 9}>{prayerShort(p.prayer)}</text>
              )}
            </g>
          );
        })}

        <circle className="arc__now" cx={here.x} cy={here.y} r="4.5" />
      </svg>
    </div>
  );
};

DayArc.propTypes = {
  prayers:  PropTypes.array,
  tz:       PropTypes.string,
  nextName: PropTypes.string,
};

export default DayArc;
